import { SetStateAction, useMemo, useRef } from 'react';

import {
  Box,
  Checkbox,
  Flex,
  FormControl,
  Grid,
  GridItem,
  Hide,
  Icon,
  Input,
  Show,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { Tooltip } from '@chakra-ui/react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { zodResolver } from '@hookform/resolvers/zod';
import ArrowIcon from 'images/icons/arrow-right.svg';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import CalendarIcon from 'public/assets/icons/calendar.svg';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/atoms';
import { FormErrorMessage } from '@/components/atoms/FormErrorMessage/FormErrorMessage';
import { FileUpload } from '@/components/sections/contact/ContactForm/FileUpload';
import { ButtonVariant } from '@/graphql/generated';
import { useSendEmail } from '@/hooks/use-send-email';
import { LOCALES } from '@/utils/constants';
import { uploadFile } from '@/utils/fileUpload';
import { appendFormMessage } from '@/utils/helper-functions/user-form';
import { getParsedUTMParams } from '@/utils/marketing';
import { GeneralPath } from '@/utils/models';
import { MAX_MESSAGE_CHARACTERS, contactFormSchema } from '@/utils/validation-schemas';

import { TopicBadge } from './TopicBadge/TopicBadge';

type ContactFormData = z.infer<typeof contactFormSchema>;

export const ContactForm = ({ setCalendlyOpen }: { setCalendlyOpen: (value: SetStateAction<boolean>) => void }) => {
  const router = useRouter();
  const captchaRef = useRef<HCaptcha>(null);

  const toast = useToast({ duration: 5000 });
  const { t } = useTranslation([LOCALES.CONTACT]);

  const PURPOSE_BUTTONS = useMemo(
    () => [
      { label: `${t('purposeBtn.design')}`, value: 'design' },
      { label: `${t('purposeBtn.development')}`, value: 'development' },
      { label: `${t('purposeBtn.talk')}`, value: 'talk' },
      { label: `${t('purposeBtn.not_sure')}`, value: 'not_sure' },
    ],
    [t],
  );

  const {
    register,
    setValue,
    resetField,
    clearErrors,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    mode: 'onChange',
    defaultValues: {
      purpose: 'working_together',
      attachments: [],
      captchaToken: '',
    },
  });

  const watchPurpose = watch('purpose');
  const watchMessage = watch('message');
  const { mutate, isLoading: submitContactFormLoading } = useSendEmail();

  const onSubmit = handleSubmit(async (values) => {
    const formValues = structuredClone(values);
    const utmParams = getParsedUTMParams();

    if (utmParams) {
      formValues.message = appendFormMessage(formValues.message, utmParams);
    }

    if (!formValues.captchaToken) {
      const captchaResult = await captchaRef.current?.execute({ async: true });
      if (captchaResult?.response) {
        formValues.captchaToken = captchaResult.response;
      } else {
        resetCaptcha();
        toast({
          status: 'error',
          title: `${t('formErrorCaptcha')}`,
        });
      }
    }

    const filePromises = values.attachments.map((file: File) => uploadFile(file));
    const fileResponses = await Promise.all(filePromises);

    const formData = new FormData();
    delete formValues.attachments;
    for (const key in formValues) {
      formData.append(key, formValues[key as keyof typeof formValues]);
    }
    if (fileResponses) {
      formData.append('attachmentLinks', fileResponses.map((link) => link.toString()).join(', '));
    }
    mutate(formData, {
      onSuccess: () => {
        router.push(GeneralPath.THANKS);
      },
      onError: () => {
        resetCaptcha();
        toast({
          status: 'error',
          title: `${t('formError')}`,
        });
      },
    });
  });

  const onVerifyCaptcha = (token: string): void => {
    setValue('captchaToken', token);
    clearErrors('captchaToken');
  };

  const resetCaptcha = (): void => {
    captchaRef.current?.resetCaptcha();
    resetField('captchaToken');
  };

  const onPurposeSelect = (value: string) => {
    setValue('purpose', value);
  };

  return (
    <>
      <Grid templateColumns="1fr" gridAutoRows={'1fr'} position="relative">
        {/* shadow behind form */}
        <GridItem
          colStart={1}
          rowStart={1}
          zIndex={-1}
          backgroundColor={'lime.500'}
          transform={{ base: 'rotate(-0.943deg)', md: 'rotate(-3.597deg)' }}
          borderRadius={'24px'}
        />
        <GridItem colStart={1} rowStart={1}>
          <Flex
            flexDir="column"
            gridColumn={1}
            borderRadius="24px"
            backgroundColor="#FFF"
            pt={{
              base: 6,
              sm: 8,
              md: 14,
            }}
            pb={{
              base: 6,
              xs: 16,
              md: 20,
            }}
            px={{
              base: 4,
              xs: 8,
              md: 14,
            }}
          >
            <Box as="form" position="relative" onSubmit={onSubmit} noValidate>
              <FormControl>
                <Flex
                  flexDir="column"
                  gap={{
                    base: 1,
                  }}
                >
                  <Flex
                    flex={1}
                    alignItems={{
                      base: 'flex-start',
                      xs: 'center',
                    }}
                    flexDir={{
                      base: 'column',
                      xs: 'row',
                    }}
                    minW="240px"
                    gap={{
                      base: 1,
                      xs: 4,
                    }}
                    fontSize="14px"
                    mb={'10px'}
                  >
                    <Flex flexDir="column" flex={1} w="100%">
                      <Text as="label" whiteSpace="nowrap" color="gray.700">
                        {t('formFields.name')}
                      </Text>{' '}
                      <Flex flex={1} flexDir="column" position="relative" w="100%">
                        <Input
                          {...register('name')}
                          type="text"
                          disabled={submitContactFormLoading}
                          aria-invalid={!!errors.name}
                          width="100%"
                          isInvalid={!!errors.name}
                          errorBorderColor="red.300"
                        />
                        <FormErrorMessage errorMessage={errors.name} />
                      </Flex>
                    </Flex>
                    <Flex flexDir="column" flex={1} w="100%">
                      <Text as="label" whiteSpace="nowrap" color="gray.700">
                        {t('formFields.email')}
                      </Text>
                      <Flex flex={1} position="relative" w="100%">
                        <Input
                          {...register('email')}
                          type="email"
                          disabled={submitContactFormLoading}
                          aria-invalid={!!errors.email}
                          isInvalid={!!errors.email}
                          flex={1}
                          errorBorderColor="red.300"
                        />
                        <FormErrorMessage errorMessage={errors.email} />
                      </Flex>
                    </Flex>
                    <Flex flexDir="column" flex={1} w="100%">
                      <Text as="label" whiteSpace="nowrap" color="gray.700">
                        {t('formFields.phone')}{' '}
                        <Text as="span" opacity="0.5">
                          ({t('formFields.optional')})
                        </Text>
                      </Text>
                      <Flex position="relative" flex={1} w="100%">
                        <Tooltip label={t('formFields.phoneTooltip')} fontSize="md">
                          <Input
                            {...register('phoneNumber')}
                            type="tel"
                            disabled={submitContactFormLoading}
                            aria-invalid={!!errors.phoneNumber}
                            isInvalid={!!errors.phoneNumber}
                            errorBorderColor="red.300"
                            flex={1}
                          />
                        </Tooltip>
                        <FormErrorMessage errorMessage={errors.phoneNumber} />
                      </Flex>
                    </Flex>
                  </Flex>
                  <Text as="label" whiteSpace="nowrap" color="gray.700">
                    {t('formFields.message')}{' '}
                    <Text as="span" opacity="0.5">
                      ({t('formFields.optional')})
                    </Text>
                  </Text>

                  <Box mb={4} position="relative" w="100%">
                    <Textarea
                      {...register('message')}
                      color="gray.700"
                      placeholder={t('formFields.message')}
                      disabled={submitContactFormLoading}
                      aria-invalid={!!errors.message}
                      isInvalid={!!errors.message}
                    />
                    <Text
                      whiteSpace="nowrap"
                      mt={1}
                      color={watchMessage && watchMessage.length > MAX_MESSAGE_CHARACTERS ? 'red.300' : 'gray.700'}
                      fontSize="12px"
                    >
                      {watchMessage?.length || 0}/{MAX_MESSAGE_CHARACTERS} {t('formFields.characters')}
                    </Text>
                    <FormErrorMessage errorMessage={errors.message} />
                  </Box>
                </Flex>

                <Flex
                  mb={7}
                  alignItems={{
                    base: 'flex-start',
                    xs: 'center',
                  }}
                  flexDir={{
                    base: 'column',
                    xs: 'row',
                  }}
                  gap={'8px'}
                >
                  <Text
                    color="gray.700"
                    variant={{
                      base: 'text-sm/lineHeight-5/font-medium',
                    }}
                    as="div"
                    fontSize="14px"
                  >
                    {t('formFields.talkAbout')}
                  </Text>
                  <Flex gap={'8px'} flexWrap={'wrap'} justifyContent="flex-start">
                    {PURPOSE_BUTTONS.map((button) => (
                      <TopicBadge
                        selected={watchPurpose === button.value}
                        label={button.label}
                        onClick={() => onPurposeSelect(button.value)}
                        key={button.label}
                      />
                    ))}
                  </Flex>
                </Flex>

                <Flex mb={7} width="100%" flexDir="column">
                  <Text
                    color="gray.700"
                    variant={{
                      base: 'text-sm/lineHeight-5/font-medium',
                    }}
                    fontSize="14px"
                    as="div"
                    display="inline"
                  >
                    {t('formFields.attachments')}{' '}
                    <Text as="span" opacity="0.5">
                      ({t('formFields.optional')})
                    </Text>
                  </Text>
                  <FileUpload
                    trigger={trigger}
                    errors={errors}
                    loading={submitContactFormLoading}
                    setValue={setValue}
                  />
                </Flex>
                <Flex
                  mb={{ base: '20px', md: '0px' }}
                  flexDirection={{ base: 'column', md: 'row' }}
                  justifyContent={{ base: 'center', md: 'space-between' }}
                >
                  <Box maxWidth="300px" mb={{ base: '20px', md: '0' }}>
                    <Checkbox
                      {...register('privacyPolicy')}
                      disabled={submitContactFormLoading}
                      variant="lime"
                      size="lg"
                      colorScheme="lime"
                      isInvalid={!!errors.privacyPolicy}
                    >
                      <Text
                        as="span"
                        color={!!errors.privacyPolicy ? 'red.300' : 'gray.700'}
                        lineHeight="150%"
                        fontSize="12px"
                        fontWeight="400"
                      >
                        {t('formFields.privacyPolicy.0')}{' '}
                        <Box
                          as={Link}
                          target="_blank"
                          href={GeneralPath.PRIVACY_POLICY}
                          fontWeight="700"
                          sx={{
                            textDecoration: 'underline',
                          }}
                        >
                          {t('formFields.privacyPolicy.1')}
                        </Box>
                        <Text display="inline"> {t('formFields.privacyPolicy.2')}</Text>
                      </Text>
                    </Checkbox>
                  </Box>
                  <Box mb={{ base: '20px', md: '0' }}>
                    <HCaptcha
                      ref={captchaRef}
                      sitekey={process.env.NEXT_PUBLIC_HCAPTCHA_SITEKEY as string}
                      onVerify={onVerifyCaptcha}
                    />
                  </Box>
                </Flex>
              </FormControl>

              <Flex
                mt={6}
                position="absolute"
                bottom={{
                  base: -10,
                  md: -24,
                }}
                gap={{
                  md: '16px',
                }}
                width="100%"
                flex={1}
                justifyContent={{ base: 'center', md: 'flex-start' }}
              >
                <Button
                  type="submit"
                  isLoading={submitContactFormLoading}
                  variant={ButtonVariant.Light}
                  fontSize="lg"
                  size="lg"
                  flexShrink={0}
                  rightIcon={<Icon as={ArrowIcon} transform="rotate(-45deg)" />}
                >
                  {t('formFields.submit')}
                </Button>
                <Show above="md">
                  <Button
                    label={t('calendarBtn')}
                    variant={ButtonVariant.Dark}
                    size="lg"
                    flexShrink={0}
                    fontSize="lg"
                    rightIcon={<CalendarIcon />}
                    type="button"
                    onClick={() => setCalendlyOpen(true)}
                  />
                </Show>
              </Flex>
            </Box>
          </Flex>
        </GridItem>
      </Grid>
      <Hide above="md">
        <Flex mt={10} justifyContent="center">
          <Button
            type="submit"
            label={t('calendarBtn')}
            variant={ButtonVariant.Dark}
            size="lg"
            fontSize="lg"
            flexShrink={0}
            rightIcon={<CalendarIcon />}
            onClick={() => setCalendlyOpen(true)}
          />
        </Flex>
      </Hide>
    </>
  );
};
